import type { Money, Sizing } from '../../api';

import { parseWireDecimal } from './exact-decimal';
import { type ReferenceLine, referenceLine } from './preview-reference';
import type { RiskPreviewState } from './risk-preview-state';
import { type FormVariant, sameSizing, sameSizingValue } from './settings-draft';

/**
 * Risk per trade, in % of equity, above which E7 asks the trader to confirm before saving
 * (P11 R1). Strictly above: 3 % itself saves at once. A friction, never a bound: the bounds stay
 * those of M2 R20 (Q46) and no value is capped.
 */
// TODO(Q260): threshold value, E5, fixed-lot rule and the no-re-ask rule are provisional.
export const HIGH_RISK_CONFIRM_PERCENT = 3;

/** What the confirmation sheet says (P11 §4). */
export type HighRiskConfirm =
  | {
      readonly kind: 'riskPercent';
      /** The typed value, as sent (never rounded). */
      readonly riskPercent: string;
      /** The preview of exactly this size; `null` when not at hand (R8, never made up). */
      readonly estimatedLoss: Money | null;
    }
  | {
      readonly kind: 'fixedLot';
      readonly lots: string;
      /** Stop of the worked example (FXM-02 §4), from the preview. */
      readonly stopLossPips: string;
      readonly estimatedLoss: Money;
      /** `estimatedLoss / equity` in basis points, rounded half-up: display only. */
      readonly equityShareBps: number;
      /**
       * The line naming the example's instrument and why (P9 R1–R3, Q32): a fixed lot's amount
       * depends on it. The same line as under the preview.
       */
      readonly reference: ReferenceLine;
    };

/** Input of {@link needsHighRiskConfirm}. */
export interface HighRiskInput {
  readonly variant: FormVariant;
  /** Size of the settings version about to be sent; `null` when only the limit is sent. */
  readonly sending: Sizing | null;
  /** Size of the saved version; `null` for a channel never set up. */
  readonly saved: Sizing | null;
  /** The form's preview; used only when it was computed for exactly `sending`. */
  readonly preview: RiskPreviewState;
}

/**
 * Whether pressing Save on E7 must first ask the trader to confirm a high risk, and what the
 * sheet says (P11 R1–R8, Proposal, to be validated, Q260). `null`: save at once.
 *
 * - E5 (`firstSetup`) never asks (R2).
 * - Only a new size asks: a limits-only save, or a size equal in value to the saved one
 *   ("10" and "10.00"), does not ask again (R7).
 * - Risk %: asks strictly above the threshold, compared exactly on the decimal (R3).
 * - Fixed lot: asks when the preview's loss at the example stop is strictly above the threshold
 *   share of the preview's equity; with no usable preview there is nothing to compare (R4).
 *
 * Integer arithmetic only (standards §1).
 */
export function needsHighRiskConfirm({
  variant,
  sending,
  saved,
  preview,
}: HighRiskInput): HighRiskConfirm | null {
  if (variant !== 'settings' || sending === null) return null;
  if (saved !== null && sameSizingValue(sending, saved)) return null;
  const current =
    preview.status === 'ready' && sameSizing(sending, preview.sizing) ? preview.preview : null;

  if (sending.mode === 'RISK_PERCENT') {
    const percent = parseWireDecimal(sending.riskPercent);
    const threshold = BigInt(HIGH_RISK_CONFIRM_PERCENT) * 10n ** BigInt(percent.scale);
    if (percent.units <= threshold) return null;
    return {
      kind: 'riskPercent',
      riskPercent: sending.riskPercent,
      estimatedLoss: current?.estimatedLoss ?? null,
    };
  }

  if (current === null) return null;
  const { equity, estimatedLoss } = current;
  if (equity.amountMinor <= 0 || equity.currency !== estimatedLoss.currency) return null;
  const loss = BigInt(estimatedLoss.amountMinor);
  const capital = BigInt(equity.amountMinor);
  if (loss * 100n <= BigInt(HIGH_RISK_CONFIRM_PERCENT) * capital) return null;
  return {
    kind: 'fixedLot',
    lots: sending.lots,
    stopLossPips: current.stopLossPips,
    estimatedLoss,
    equityShareBps: Number((2n * loss * 10_000n + capital) / (2n * capital)),
    reference: referenceLine(current, 'FIXED_LOT'),
  };
}
